import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { number, object, string, enum as zEnum } from "zod/v4";
import { ResponseLength } from "../conversation";
import { PredefinedChipOptions } from "../customer-widget/customer-widget";
export const DEFAULT_QUESTION_PROMPT_COUNT = 3;

export const ConciergeBehaviorSettingsParser = object({
  name: string().max(100).optional(),
  welcomeMessage: string().max(500).optional(),
  toneOfVoice: string().max(500).optional(),
  brandDescription: string().max(1000).optional(),
  customerDescription: string().max(1000).optional(),
  outputLength: zEnum(ResponseLength).optional(),
});

export type ConciergeBehaviorSettings = z.infer<
  typeof ConciergeBehaviorSettingsParser
>;

export const ConciergeProductFormSettingsParser = object({
  numQuestionPrompts: number().min(0).max(5).optional(),
});

const EmailSignUpParser = object({
  incentivize: z.boolean(),
  buttonLabel: z.string(),
  signUpDiscountAmount: z.string(),
});

export const conciergeEmailSignUpDefault = {
  incentivize: false,
  buttonLabel: "Earn a 15% discount when you sign in",
  signUpDiscountAmount: "15",
};

export const proactiveChipEngagementDefault = false;

export const ConciergeCustomerWidgetSettingsParser = object({
  chips: z.object({
    selectedPredefinedChips: z.array(zEnum(PredefinedChipOptions)).optional(),
    customChips: z.array(z.string()).optional(),
  }),
  emailSignUp: EmailSignUpParser.optional(),
  proactiveChipEngagement: z.boolean().optional(),
});

export enum TicketHandoffType {
  LIVE_CHAT = "LIVE_CHAT",
  EMAIL = "EMAIL",
}

const TicketHandoffSettingsParser = object({
  handoffType: zEnum(TicketHandoffType).optional(),
  emailHandoffMessage: string().optional(),
  forwardToExternalEmail: z.boolean().optional(),
});

const NarrowProductRecommendationParser = object({
  enabled: z.boolean().default(false),
  productCount: z.number().min(1).max(5).default(5),
});

export const ConciergeGeneralSettingsParser = object({
  brandAndVoiceSettings: ConciergeBehaviorSettingsParser,
  productFormSettings: ConciergeProductFormSettingsParser,
  customerWidgetSettings: ConciergeCustomerWidgetSettingsParser,
  ticketHandoffSettings: TicketHandoffSettingsParser.nullable().optional(),
  overriddenBestSellers: z.array(z.string()).optional(),
  excludedProductTags: z.array(z.string()).optional(),
  narrowProductRecommendation: NarrowProductRecommendationParser.optional(),
});

export type ConciergeGeneralSettings = z.infer<
  typeof ConciergeGeneralSettingsParser
>;

export const TimeUnitEnum = createEnum(["hours", "days"]);
export type TimeUnit = z.infer<typeof TimeUnitEnum>;

export const SelfServeOnboardingSettingsParser = object({
  contactStep: z.object({ name: z.string() }),
  skillsStep: z.object({
    backInStockNotifications: z.object({ enabled: z.boolean() }),
    cancelOrder: z.object({
      enabled: z.boolean(),
      timeRange: z.object({ unit: TimeUnitEnum, value: z.number() }),
    }),
    editAddress: z.object({
      enabled: z.boolean(),
      timeRange: z.object({ unit: TimeUnitEnum, value: z.number() }),
    }),
    updateOrder: z.object({
      enabled: z.boolean(),
      timeRange: z.object({ unit: TimeUnitEnum, value: z.number() }),
    }),
    cartEditing: z.object({ enabled: z.boolean() }),
    createDiscountCode: z.object({
      enabled: z.boolean(),
      maxDiscountPercentage: z.number(),
      timeRange: z.object({ unit: TimeUnitEnum, value: z.number() }),
    }),
    productBundle: z.object({
      enabled: z.boolean(),
      maxDiscountPercentage: z.number(),
      timeRange: z.object({ unit: TimeUnitEnum, value: z.number() }),
    }),
    productVisualization: z.object({ enabled: z.boolean() }),
    webScrapingForProductInformation: z.object({ enabled: z.boolean() }),
    forceUseRetrieveSupportKnowledge: z.object({ enabled: z.boolean() }),
    returnsPortalLinkOverride: z.object({ url: z.string() }),
  }),
  personalityStep: z.object({
    toneOfVoice: z.string(),
    brandDescription: z.string(),
    customerDescription: z.string(),
  }),
  customerWidgetStep: z.object({
    color: z.string(),
    promptSettings: z.object({
      selectedPredefinedChips: z.array(zEnum(PredefinedChipOptions)),
      customChips: z.array(z.string()),
      proactiveChipEngagement: z.boolean(),
    }),
  }),
});

export type SelfServeOnboardingSettings = z.infer<
  typeof SelfServeOnboardingSettingsParser
>;
